import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveHadasimAction } from '../../../actions/saveHadasimAction';
import './CreateHadasimPage.scss';

interface HadasimItem {
  id: number;
  kind: string;
  price: number | string;
  year?: string;
  active?: boolean;
}

const CreateHadasimPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [hadasims, setHadasims] = useState<HadasimItem[]>([]);
  const [kind, setKind] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchHadasims = () => {
    axios
      .get('/api/v1/admin/hadasims', headers)
      .then(({ data }) => {
        setHadasims(data);
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data && data.hadasims) {
            setHadasims(data.hadasims);
          }
        });
      });
  };

  useEffect(() => {
    fetchHadasims();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind.trim() || !price) return;

    setLoading(true);
    dispatch(
      saveHadasimAction(kind, price, headers, () => {
        setKind('');
        setPrice('');
        setLoading(false);
        fetchHadasims();
      })
    );
  };

  const handleToggleActive = (id: number, newActive: boolean) => {
    setHadasims(prev => prev.map(item => (item.id === id ? { ...item, active: newActive } : item)));
    axios.patch(`/api/v1/admin/hadasims/${id}`, { active: newActive }, headers).catch(() => {
      setHadasims(prev => prev.map(item => (item.id === id ? { ...item, active: !newActive } : item)));
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Hadasim item?')) return;
    setHadasims(prev => prev.filter(item => item.id !== id));
    axios.delete(`/api/v1/admin/hadasims/${id}`, headers).catch(() => {
      alert('Failed to delete Hadasim item');
      fetchHadasims();
    });
  };

  return (
    <div className="container admin-item-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage & Create Hadasim</h3>

        <div className="form-section">
          <h5 className="mb-3">Add New Hadasim Item</h5>
          <form onSubmit={handleSubmit} className="inline-form">
            <div style={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Kind"
                value={kind}
                onChange={e => setKind(e.target.value)}
                required
              />
            </div>
            <div style={{ width: '120px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Price ($)"
                value={price}
                onChange={e => setPrice(e.target.value.replace(/[^0-9]/, ''))}
                required
              />
            </div>
            <div className="submit-btn-container">
              <button
                type="submit"
                className="btn btn-success"
                disabled={!kind.trim() || !price || loading}>
                {loading ? 'Saving...' : 'Save Hadasim'}
              </button>
            </div>
          </form>
        </div>

        <h5>Current Hadasim Items ({hadasims.length})</h5>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>Price</th>
              <th>Year</th>
              <th>Active</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {hadasims.map(hadasim => (
              <tr key={`hadasim-${hadasim.id}`}>
                <td>{hadasim.id}</td>
                <td>{hadasim.kind}</td>
                <td>${hadasim.price}</td>
                <td>{hadasim.year || new Date().getFullYear()}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={hadasim.active ?? true}
                    onChange={e => handleToggleActive(hadasim.id, e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <i
                    className="fa delete-icon"
                    title="Delete Hadasim"
                    onClick={() => handleDelete(hadasim.id)}>
                    &#xf014;
                  </i>
                </td>
              </tr>
            ))}
            {hadasims.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No Hadasim found for current year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateHadasimPage;
